import type { FC, ReactNode } from "react"
import { memo, useMemo } from "react"
import { useQuery } from "@rocicorp/zero/react"

import type { FormattedAttachment } from "@productlane/ui"
import type { LiveChatMessage } from "@productlane/zero"
import { generateNameFromEmail } from "@productlane/api/src/lib/name-from-email"
import {
	normalizeBlockElements,
	normalizePlainTextNewlines,
	paragraphsFromLooseHtml,
	removeCidImages,
	trimTrailingEmptyElements,
} from "@productlane/lib/src/html"
import { AttachmentsList, IconButton } from "@productlane/ui"
import { EllipsisIcon as Ellipsis } from "@productlane/ui/src/icons/project/ellipsis-icon"
import { queries } from "@productlane/zero/queries"

import { EmailHtml } from "@/components/email-html"
import { useThreadContext } from "../../../thread-context"
import { AgentMessageAvatar } from "./base/agent-message-avatar"
import { SlackStyleMessage } from "./base/slack-style-message"
import { DeviceInfoLine } from "./device-info-line"
import { AI_CHAT_ACTOR_NAME, isAgentLiveChatReply } from "./improve-reply"
import { ImproveReplyButton } from "./improve-reply-panel"
import { LiveChatMessageActions } from "./live-chat-message-actions"
import { useMailCollapse } from "./use-mail-collapse"

interface Props {
	message: LiveChatMessage
	contactIsUnverified: boolean
	showAvatar?: boolean
	deviceInfoText?: string
}

const EMPTY_ATTACHMENTS: [] = []

const prepareContent = (content: string) =>
	removeCidImages(normalizePlainTextNewlines(content))

// Runs after the quote/signature split: flattening blocks first would strip the
// id/class anchors (#Signature, #appendonsend) that splitEmailChunks matches on.
const flattenBlocks = (html: string) =>
	trimTrailingEmptyElements(normalizeBlockElements(html))

export const LiveChatMessageEvent: FC<Props> = memo(
	({ message, contactIsUnverified, showAvatar = true, deviceInfoText }) => {
		const { thread } = useThreadContext()
		const [contact] = useQuery(
			queries.contactByEmail((message?.email || "").toLowerCase().trim()),
		)

		const isFromSystem = message.role === "SYSTEM"
		const isFromAi = message.role === "AI"
		const isEmailedRole = message.role === "AGENT" || isFromAi
		const isContact = !isFromAi && !isFromSystem && !!contact
		const isUnverified = contactIsUnverified && isContact

		const isRealName = (name?: string | null): boolean =>
			!!name && !name.includes("@")
		const emailLike = (value?: string | null): string | undefined =>
			value && value.includes("@") ? value : undefined
		const resolvedEmail =
			contact?.email ||
			message.email ||
			emailLike(contact?.name) ||
			emailLike(message.name)
		// The agent's own reply carries the workspace's name on the row, not its own.
		const contactName = isFromAi
			? AI_CHAT_ACTOR_NAME
			: (isRealName(contact?.name) && contact?.name) ||
				(isRealName(message.name) && message.name) ||
				(resolvedEmail ? generateNameFromEmail(resolvedEmail) : undefined) ||
				(isFromAi || isFromSystem ? "Productlane Agent" : "Agent")

		const companyLogoUrl = thread.company?.logoUrl || undefined
		const companyName = thread.company?.name || undefined

		const preparedContent = useMemo(
			() =>
				prepareContent(
					isFromAi ? paragraphsFromLooseHtml(message.content) : message.content,
				),
			[message.content, isFromAi],
		)

		const { isCollapsed, toggleCollapse, htmlMailChunks } = useMailCollapse(
			preparedContent,
			EMPTY_ATTACHMENTS,
		)

		const visibleHtml = useMemo(
			() => (htmlMailChunks[0] ? flattenBlocks(htmlMailChunks[0]) : ""),
			[htmlMailChunks],
		)
		const hiddenHtml = useMemo(
			() =>
				htmlMailChunks[1] && !isCollapsed
					? flattenBlocks(htmlMailChunks[1])
					: "",
			[htmlMailChunks, isCollapsed],
		)

		const sortedAttachments = useMemo(() => {
			return (message.attachments ?? [])
				.map((attachment) => {
					return (
						typeof attachment === "string" ? JSON.parse(attachment) : attachment
					) as FormattedAttachment
				})
				.sort((a, b) => {
					if (a.ContentType.includes("video")) return -1
					if (b.ContentType.includes("video")) return 1
					if (a.ContentType.includes("image")) return -1
					if (b.ContentType.includes("image")) return 1
					return 0
				})
		}, [message.attachments])

		const renderAvatarContent = (): ReactNode => {
			if (isFromAi || isFromSystem) {
				return <AgentMessageAvatar />
			}
			return undefined
		}

		const canRetract = message.role === "AGENT" || isFromAi

		return (
			<div className="group/live-chat-message">
				<SlackStyleMessage
					showAvatar={showAvatar}
					senderName={contactName}
					senderEmail={contact?.email ?? message.email}
					senderImage={contact?.imageUrl || message.imageUrl}
					timestamp={new Date(message.createdAt)}
					avatarContent={renderAvatarContent()}
					senderIsUnverified={isUnverified}
					readAt={
						message.role === "AGENT"
							? message.readAt
								? new Date(message.readAt)
								: null
							: undefined
					}
					emailedAt={
						isEmailedRole && message.emailSentAt
							? new Date(message.emailSentAt)
							: undefined
					}
					emailedTo={thread.contact?.email}
					companyName={companyName}
					companyLogoUrl={companyLogoUrl}
					source="live-chat"
					isOutbound={message.role !== "USER"}
					actions={
						canRetract ? (
							<LiveChatMessageActions messageId={message.id} />
						) : undefined
					}
					footer={
						deviceInfoText ? (
							<DeviceInfoLine text={deviceInfoText} />
						) : undefined
					}
				>
					{visibleHtml && <EmailHtml html={visibleHtml} />}

					{htmlMailChunks[1] && (
						<div>
							<IconButton
								variant="link"
								className="-ml-2"
								onClick={toggleCollapse}
								Icon={Ellipsis}
							/>
							{hiddenHtml && <EmailHtml html={hiddenHtml} />}
						</div>
					)}

					{message.attachments && message.attachments.length > 0 && (
						<>
							{message.content && message.content !== "<br>" && (
								<hr className="my-3" />
							)}
							<AttachmentsList
								enableViewer
								showPointer={false}
								attachments={sortedAttachments.map((attachment) => ({
									...attachment,
									Content: attachment.Content ?? "",
								}))}
							/>
						</>
					)}

					{isAgentLiveChatReply(message) && (
						<ImproveReplyButton
							threadId={thread.id}
							channel="liveChat"
							messageId={message.id}
						/>
					)}
				</SlackStyleMessage>
			</div>
		)
	},
)
